"use client";

import { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Package,
  ShoppingCart,
  Users,
  Tag,
  MessageSquare,
  Wallet,
  Mail,
  FileText,
  FolderOpen,
  Database,
} from "lucide-react";

export type EmptyStateType =
  | "products"
  | "orders"
  | "customers"
  | "coupons"
  | "tickets"
  | "wallet"
  | "campaigns"
  | "templates"
  | "categories"
  | "cart"
  | "general"
  | "inventory";

interface EmptyStateProps {
  type?: EmptyStateType;
  title?: string;
  description?: string;
  action?: {
    label: string;
    onClick: () => void;
    variant?:
      | "default"
      | "outline"
      | "secondary"
      | "ghost"
      | "link"
      | "destructive";
  };
  icon?: ReactNode;
  className?: string;
  size?: "sm" | "md" | "lg";
}

const defaultContent: Record<
  EmptyStateType,
  { title: string; description: string }
> = {
  products: {
    title: "No products found",
    description: "Get started by adding your first product to your store.",
  },
  orders: {
    title: "No orders yet",
    description: "When customers place orders, they'll appear here.",
  },
  customers: {
    title: "No customers found",
    description:
      "Your customer list will appear here once they start signing up.",
  },
  coupons: {
    title: "No coupons created",
    description: "Create discount codes to boost sales and reward customers.",
  },
  tickets: {
    title: "No support tickets",
    description: "Customer support requests will appear here.",
  },
  wallet: {
    title: "No transactions",
    description: "Your wallet transactions will appear here.",
  },
  campaigns: {
    title: "No marketing campaigns",
    description: "Create email campaigns to engage with your customers.",
  },
  templates: {
    title: "No templates found",
    description: "Design templates will appear here once you create them.",
  },
  categories: {
    title: "No categories found",
    description: "Organize your products with categories.",
  },
  cart: {
    title: "No abandoned carts",
    description: "Abandoned cart data will appear here.",
  },
  general: {
    title: "No data found",
    description: "The information you're looking for isn't here yet.",
  },
  inventory: {
    title: "No inventory items",
    description: "No inventory variants found. Update stock or refresh to load inventory.",
  },
};

const iconMap: Record<EmptyStateType, ReactNode> = {
  products: <Package className="h-full w-full text-muted-foreground/40" />,
  orders: <ShoppingCart className="h-full w-full text-muted-foreground/40" />,
  customers: <Users className="h-full w-full text-muted-foreground/40" />,
  coupons: <Tag className="h-full w-full text-muted-foreground/40" />,
  tickets: <MessageSquare className="h-full w-full text-muted-foreground/40" />,
  wallet: <Wallet className="h-full w-full text-muted-foreground/40" />,
  campaigns: <Mail className="h-full w-full text-muted-foreground/40" />,
  templates: <FileText className="h-full w-full text-muted-foreground/40" />,
  categories: <FolderOpen className="h-full w-full text-muted-foreground/40" />,
  cart: <ShoppingCart className="h-full w-full text-muted-foreground/40" />,
  general: <Database className="h-full w-full text-muted-foreground/40" />,
  inventory: <Package className="h-full w-full text-muted-foreground/40" />,
};

const sizeConfig = {
  sm: {
    container: "py-8",
    illustration: "w-16 h-16",
    title: "text-base",
    description: "text-sm",
    spacing: "space-y-3",
  },
  md: {
    container: "py-12",
    illustration: "w-24 h-24",
    title: "text-lg",
    description: "text-sm",
    spacing: "space-y-4",
  },
  lg: {
    container: "py-16",
    illustration: "w-32 h-32",
    title: "text-xl",
    description: "text-base",
    spacing: "space-y-6",
  },
};

export function EmptyState({
  type = "general",
  title,
  description,
  action,
  icon,
  className,
  size = "md",
}: EmptyStateProps) {
  const config = sizeConfig[size];
  const defaultData = defaultContent[type];
  const defaultIcon = iconMap[type];

  const displayTitle = title || defaultData.title;
  const displayDescription = description || defaultData.description;

  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        config.container,
        config.spacing,
        className,
      )}
    >
      {/* Illustration */}
      <div
        className={cn(
          "flex items-center justify-center mb-2",
          config.illustration,
        )}
      >
        <div className="w-full h-full rounded-full bg-muted/50 flex items-center justify-center border-2 border-dashed border-muted-foreground/20">
          {icon || defaultIcon}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-sm space-y-2">
        <h3 className={cn("font-semibold text-muted-foreground", config.title)}>
          {displayTitle}
        </h3>
        <p
          className={cn(
            "text-muted-foreground/80 leading-relaxed",
            config.description,
          )}
        >
          {displayDescription}
        </p>
      </div>

      {/* Action Button */}
      {action && (
        <Button
          onClick={action.onClick}
          variant={action.variant || "default"}
          className="mt-2"
        >
          {action.label}
        </Button>
      )}
    </div>
  );
}

export default EmptyState;
